import { useMemo, useState } from "react"
import Command from "@/v2-ui/command"
import Icon from "@/v2-ui/icon"
import cn from "@/v2-ui/utils/utils.cn"
import { usePrimitiveText } from "@/v2-ui/text/offline"
import {
  isMultiSelectBranch,
  type MultiSelectOption,
  type MultiSelectText,
} from "./MultiSelect"
import { highlightLabel } from "./multiSelect.highlight"

// Tree body for MultiSelect: rendered instead of the flat cmdk list when any
// option carries `children` (hierarchical catalogs — NACE codes, building
// types). Selection currency is LEAF values only: toggling a parent toggles
// its descendant leaves, and the parent checkbox derives checked/indeterminate
// from how many of its leaves are selected. Children are NOT mounted until
// their node is expanded (or revealed by search), so a 700-node catalog opens
// as a dozen rows. An ASYNC branch (`hasChildren` without loaded children)
// renders expandable too: opening it fires `onExpand` and shows a loading
// row until the consumer serves `children`; until then the node is its own
// selection unit (its value), so a big leaf level (15k grunnkretser) never
// has to ship up front.

type TreeRow = {
  option: MultiSelectOption
  depth: number
  /** Descendant leaf values (self when the node is a leaf) — the selection unit. */
  leafValues: string[]
  /** Placeholder row under an open async branch whose children are loading. */
  isLoadingPlaceholder?: boolean
}

export function collectLeafValues(option: MultiSelectOption): string[] {
  if (!option.children || option.children.length === 0) return [option.value]
  return option.children.flatMap(collectLeafValues)
}

/**
 * The selection as the fewest nodes that describe it, top-down: a node whose
 * leaves are ALL selected stands in for its subtree, a partially selected
 * node yields to its children, an unselected one is skipped. Display-only
 * (trigger chips / summary) — the selection currency stays leaf values, same
 * predicate the tree panel uses for its checked/indeterminate parents.
 */
export function collapseSelectedOptions(
  options: MultiSelectOption[],
  selectedSet: ReadonlySet<string>,
): MultiSelectOption[] {
  const out: MultiSelectOption[] = []
  for (const option of options) {
    const leafValues = collectLeafValues(option)
    if (leafValues.every((v) => selectedSet.has(v))) {
      out.push(option)
    } else if (option.children?.length) {
      out.push(...collapseSelectedOptions(option.children, selectedSet))
    }
  }
  return out
}

function nodeMatches(option: MultiSelectOption, term: string): boolean {
  const haystack = [option.label, option.value, ...(option.keywords ?? [])]
  return haystack.some((h) => h.toLowerCase().includes(term))
}

/** True when the node or any descendant matches the search term. */
function subtreeMatches(option: MultiSelectOption, term: string): boolean {
  if (nodeMatches(option, term)) return true
  return (option.children ?? []).some((child) => subtreeMatches(child, term))
}

export type MultiSelectTreePanelProps = {
  value: string[]
  onChange: (value: string[]) => void
  options: MultiSelectOption[]
  text?: MultiSelectText
  searchPlaceholder?: string
  emptyMessage?: React.ReactNode
  enableSelectAll?: boolean
  autoFocusSearch?: boolean
  highlightSearch?: boolean
  /** A branch was opened (caret or row click); async branches fetch here. */
  onExpand?: (option: MultiSelectOption) => void
  /** The panel's own search term changed (notification only, the tree keeps
   *  the term): a consumer with async branches fetches what it may reveal. */
  onSearchChange?: (search: string) => void
  /** A trailing loading row while async branches or a search are fetched. */
  isLoading?: boolean
}

export function MultiSelectTreePanel(props: MultiSelectTreePanelProps) {
  const { getText } = usePrimitiveText()
  const {
    value,
    onChange,
    options,
    text,
    searchPlaceholder = getText("UI_Primitive_MultiSelect_SearchPlaceholder")
      .Name,
    emptyMessage = getText("UI_Primitive_MultiSelect_NoMatches").Name,
    enableSelectAll = true,
    autoFocusSearch,
    highlightSearch = true,
    onExpand,
    onSearchChange,
    isLoading,
  } = props
  const selectAllLabel =
    text?.selectAll ?? getText("UI_Primitive_MultiSelect_SelectAll").Name
  const clearLabel =
    text?.clear ?? getText("UI_Primitive_MultiSelect_Clear").Name

  const selectedSet = useMemo(() => new Set(value), [value])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [search, setSearchState] = useState("")
  const term = search.trim().toLowerCase()
  function setSearch(next: string) {
    setSearchState(next)
    onSearchChange?.(next)
  }

  // Visible rows: roots plus the children of expanded nodes. While searching,
  // a node is visible when its subtree matches, and matching ancestors render
  // expanded so the hit's chain is on screen — collapsed branches stay
  // unmounted either way.
  const rows = useMemo<TreeRow[]>(() => {
    const out: TreeRow[] = []
    function walk(nodes: MultiSelectOption[], depth: number) {
      for (const node of nodes) {
        if (term !== "" && !subtreeMatches(node, term)) continue
        out.push({ option: node, depth, leafValues: collectLeafValues(node) })
        const hasChildren = isMultiSelectBranch(node)
        const isOpen =
          term !== ""
            ? !nodeMatches(node, term) || expanded.has(node.value)
            : expanded.has(node.value)
        if (!hasChildren || !isOpen) continue
        if ((node.children?.length ?? 0) > 0) {
          walk(node.children ?? [], depth + 1)
        } else if (node.isLoadingChildren) {
          out.push({
            option: node,
            depth: depth + 1,
            leafValues: [],
            isLoadingPlaceholder: true,
          })
        }
      }
    }
    walk(options, 0)
    return out
  }, [options, expanded, term])

  const allLeafValues = useMemo(
    () => options.flatMap(collectLeafValues),
    [options],
  )
  const allSelected =
    allLeafValues.length > 0 && allLeafValues.every((v) => selectedSet.has(v))
  const noneSelected = value.length === 0

  function toggleExpanded(option: MultiSelectOption) {
    const isOpening = !expanded.has(option.value)
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(option.value)) next.delete(option.value)
      else next.add(option.value)
      return next
    })
    if (isOpening) onExpand?.(option)
  }

  function toggleNode(row: TreeRow) {
    const allIn = row.leafValues.every((v) => selectedSet.has(v))
    if (allIn) {
      const drop = new Set(row.leafValues)
      onChange(value.filter((v) => !drop.has(v)))
    } else {
      const merged = new Set(value)
      for (const v of row.leafValues) merged.add(v)
      onChange([...merged])
    }
  }

  return (
    <Command shouldFilter={false}>
      <Command.Input
        autoFocus={autoFocusSearch}
        value={search}
        onValueChange={setSearch}
        placeholder={searchPlaceholder}
      />
      <Command.List>
        {rows.length === 0 && !isLoading ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : null}
        {rows.map((row) => {
          const { option, depth } = row
          if (row.isLoadingPlaceholder) {
            return (
              <Command.Item
                key={`${option.value}:loading`}
                value={`${option.value}:loading`}
                disabled
                aria-busy
                className="flex items-center gap-2"
                style={{ paddingLeft: `${4 + depth * 16}px` }}
              >
                <span className="size-4 shrink-0" aria-hidden />
                <Icon loading size={12} className="text-muted-foreground" />
              </Command.Item>
            )
          }
          const hasChildren = isMultiSelectBranch(option)
          const isOpen =
            term !== ""
              ? !nodeMatches(option, term) || expanded.has(option.value)
              : expanded.has(option.value)
          const selectedCount = row.leafValues.filter((v) =>
            selectedSet.has(v),
          ).length
          const isChecked =
            selectedCount > 0 && selectedCount === row.leafValues.length
          const isIndeterminate = selectedCount > 0 && !isChecked
          return (
            <Command.Item
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              // Row click NAVIGATES on parents (expand/collapse) and selects
              // only on leaves — selecting a whole subtree is an explicit
              // checkbox action, so browsing the tree never mutates the
              // filter (FUS-1441 review feedback).
              onSelect={() =>
                hasChildren ? toggleExpanded(option) : toggleNode(row)
              }
              className="flex cursor-pointer items-center gap-2"
              style={{ paddingLeft: `${4 + depth * 16}px` }}
            >
              {hasChildren ? (
                <button
                  type="button"
                  aria-label={option.label}
                  aria-expanded={isOpen}
                  data-feature-code="UI_Primitive_MultiSelect_TreeToggle"
                  className="flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
                  // Stop BOTH phases: pointerdown (cmdk arms selection
                  // there) and click (the row's onSelect fires on click) —
                  // otherwise the caret toggles and the row toggles right
                  // back, a no-op. The toggle itself runs on click only.
                  onPointerDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleExpanded(option)
                  }}
                >
                  <Icon
                    src="caret-down-custom"
                    size={12}
                    className={cn(
                      "text-current transition-transform",
                      !isOpen && "-rotate-90",
                    )}
                  />
                </button>
              ) : (
                <span className="size-4 shrink-0" aria-hidden />
              )}
              <button
                type="button"
                role="checkbox"
                aria-checked={isIndeterminate ? "mixed" : isChecked}
                aria-label={option.label}
                data-feature-code="UI_Primitive_MultiSelect_TreeCheck"
                className={cn(
                  "flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm border",
                  isChecked || isIndeterminate
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-transparent",
                )}
                // Same two-phase stop as the caret: otherwise the row's
                // click-time onSelect re-toggles right after the checkbox.
                onPointerDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleNode(row)
                }}
              >
                {isChecked ? (
                  <Icon src="check" size={12} className="text-current" />
                ) : isIndeterminate ? (
                  <span className="h-0.5 w-2 rounded-full bg-current" />
                ) : null}
              </button>
              <span className="min-w-0 flex-1 truncate">
                {/* Rich body wins over the plain label (same contract as the
                    flat panel); search highlighting only applies to labels. */}
                {option.content != null
                  ? option.content
                  : highlightSearch && term !== ""
                    ? highlightLabel(option.label, term)
                    : option.label}
              </span>
              {option.trailing != null ? (
                <span className="ml-2 shrink-0">{option.trailing}</span>
              ) : null}
            </Command.Item>
          )
        })}
        {isLoading ? (
          <div
            aria-busy
            className="flex items-center justify-center py-2 text-muted-foreground"
          >
            <Icon loading size={12} className="text-current" />
          </div>
        ) : null}
      </Command.List>
      {enableSelectAll ? (
        <div className="flex items-center justify-between gap-2 border-t border-border px-1 py-1">
          <button
            type="button"
            disabled={allSelected}
            data-feature-code="UI_Primitive_MultiSelect_SelectAll"
            className="rounded-sm px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => onChange(allLeafValues)}
          >
            {selectAllLabel}
          </button>
          <button
            type="button"
            disabled={noneSelected}
            data-feature-code="UI_Primitive_MultiSelect_Clear"
            className="rounded-sm px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => onChange([])}
          >
            {clearLabel}
          </button>
        </div>
      ) : null}
    </Command>
  )
}
